import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useGetBrandsQuery } from '@store/api/cigarApi'
import { useCreateBrandMutation } from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const BrandManagement: React.FC = () => {
  const { data: brands = [], isLoading, refetch } = useGetBrandsQuery({})
  const [createBrand, { isLoading: isCreating }] = useCreateBrandMutation()
  const [name, setName] = useState('')
  const [country, setCountry] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setMessage(null)
    try {
      await createBrand({ name: name.trim(), country: country.trim() || undefined }).unwrap()
      setName('')
      setCountry('')
      setMessage({ type: 'success', text: 'Brand created' })
      refetch()
    } catch (err: unknown) {
      const apiErr = err as { data?: { message?: string } }
      setMessage({ type: 'error', text: apiErr?.data?.message ?? 'Failed to create brand' })
    }
  }

  return (
    <AdminPageGate permission="content:write" title="Brand Catalog">
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Brand Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage shared catalog brands used by the cigar database and imports.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Brand
          </Typography>
          {message && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}
          <Box component="form" onSubmit={handleCreate}>
            <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
              <TextField
                label="Brand name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Country (optional)"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                fullWidth
              />
              <Button type="submit" variant="contained" disabled={isCreating || !name.trim()}>
                {isCreating ? 'Creating…' : 'Create'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Brands ({brands.length})
          </Typography>
          {isLoading ? (
            <CircularProgress size={28} />
          ) : (
            <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 400, overflow: 'auto' }}>
              {brands.map((brand) => (
                <Typography component="li" variant="body2" key={brand.id} sx={{ mb: 0.5 }}>
                  {brand.name}
                  {brand.country ? ` — ${brand.country}` : ''}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
    </AdminPageGate>
  )
}

export default BrandManagement
